import React, { useState } from "react";
import { CircularProgress, IconButton, TextField, Tooltip } from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import { Autocomplete } from "@material-ui/lab";
import Dropdown from "rc-dropdown";
import DefaultButton from "../../../styles/materialButtonStyle";
import {
  StyledFilterBlock, StyledFilterBlockTitle,
  StyledFilterCloseBtn, StyledFilterDropdownWrapper,
  StyledFilterGroup, StyledFilterWrapper, StyledSelectGroup, StyledShowFilterBtn,
} from "../../../elements/filter/styledFiter";

const MANAGER_TYPES = ["cash", "bank", "crypto"];
const STATUSES      = ["pending", "paid", "frozen", "error", "cancelled"];

const TelegramInvoicesFilter = ({ filterData, setFilterData, bots, botsLoading, chats, chatsLoading, managers, onOpen, t }) => {
  const DEFAULT_FILTER_DATA = {
    "currentTab": filterData.currentTab,
    "page":       1,
    "limit":      filterData.limit,
    "projectId":  null,
    "chatId":     null,
    "managerId":  null,
    "type":       null,
    "status":     null,
    "invoiceId":  null,
  };

  const [visible, setVisible] = useState(false);
  const [invoiceIdInput, setInvoiceIdInput] = useState(filterData.invoiceId || "");

  const handleVisibleChange = (val) => {
    setVisible(val);
    if (val) onOpen();
  };

  const filteredChats = filterData.projectId
    ? chats.filter(chat => String(chat.projectId) === String(filterData.projectId))
    : chats;

  const handleChange = (name, value) => {
    setFilterData(prev => ({ ...prev, [name]: value || null, page: 1 }));
  };

  const handleProjectChange = (value) => {
    setFilterData(prev => ({ ...prev, projectId: value || null, chatId: null, managerId: null, page: 1 }));
  };

  const handleApplyInvoiceId = () => {
    setFilterData(prev => ({ ...prev, invoiceId: invoiceIdInput.trim() || null, page: 1 }));
  };

  const handleClearFilter = () => {
    setInvoiceIdInput("");
    setFilterData(DEFAULT_FILTER_DATA);
  };

  const loadingEndAdornment = (params) => ({
    ...params.InputProps,
    endAdornment: (
      <>
        <CircularProgress color="inherit" size={16} />
        {params.InputProps.endAdornment}
      </>
    ),
  });

  const FilterList = () => (
    <>
      {visible && (
        <StyledFilterDropdownWrapper>
          <StyledFilterBlock actions>
            <StyledFilterGroup className="clear-btn">
              <DefaultButton color="transparent" onClick={handleClearFilter}>
                {t("invoices.filter.clearBtn")}
              </DefaultButton>
            </StyledFilterGroup>
            <StyledFilterGroup className="close-btn">
              <StyledFilterCloseBtn onClick={() => setVisible(false)} />
            </StyledFilterGroup>
          </StyledFilterBlock>

          <StyledFilterBlockTitle>{t("invoices.filter.filterBy")}:</StyledFilterBlockTitle>
          <StyledFilterBlock col="3">
            <StyledSelectGroup>
              <FormControl>
                <Autocomplete
                  options={bots}
                  loading={botsLoading}
                  value={bots.find(b => String(b.projectId) === String(filterData.projectId)) || null}
                  onChange={(e, val) => handleProjectChange(val ? String(val.projectId) : null)}
                  getOptionLabel={(option) => option.projectName}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("invoices.filter.project")}
                      InputProps={botsLoading ? loadingEndAdornment(params) : params.InputProps}
                    />
                  )}
                  blurOnSelect
                />
              </FormControl>
            </StyledSelectGroup>
            <StyledSelectGroup>
              <FormControl>
                <Autocomplete
                  options={filteredChats}
                  loading={chatsLoading}
                  value={filteredChats.find(c => String(c.id) === String(filterData.chatId)) || null}
                  onChange={(e, val) => handleChange("chatId", val ? String(val.id) : null)}
                  getOptionLabel={(option) => option.title || `#${option.id}`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("invoices.filter.chat")}
                      InputProps={chatsLoading ? loadingEndAdornment(params) : params.InputProps}
                    />
                  )}
                  blurOnSelect
                />
              </FormControl>
            </StyledSelectGroup>
            <StyledSelectGroup>
              <FormControl>
                <Autocomplete
                  options={managers}
                  value={managers.find(m => String(m.id) === String(filterData.managerId)) || null}
                  onChange={(e, val) => handleChange("managerId", val ? String(val.id) : null)}
                  getOptionLabel={(option) => option.name}
                  disabled={!filterData.projectId}
                  renderInput={(params) => <TextField {...params} label={t("invoices.filter.manager")} />}
                  blurOnSelect
                />
              </FormControl>
            </StyledSelectGroup>
          </StyledFilterBlock>

          <StyledFilterBlockTitle mt="10">{t("invoices.filter.filterByType")}:</StyledFilterBlockTitle>
          <StyledFilterBlock col="3">
            <StyledSelectGroup>
              <FormControl>
                <Autocomplete
                  options={MANAGER_TYPES}
                  value={filterData.type || null}
                  onChange={(e, val) => handleChange("type", val)}
                  getOptionLabel={(option) => option}
                  renderInput={(params) => <TextField {...params} label={t("invoices.filter.managerType")} />}
                  blurOnSelect
                />
              </FormControl>
            </StyledSelectGroup>
            <StyledSelectGroup>
              <FormControl>
                <Autocomplete
                  options={STATUSES}
                  value={filterData.status || null}
                  onChange={(e, val) => handleChange("status", val)}
                  getOptionLabel={(option) => t(`invoices.filter.${option}`)}
                  renderInput={(params) => <TextField {...params} label={t("invoices.filter.status")} />}
                  blurOnSelect
                />
              </FormControl>
            </StyledSelectGroup>
            <StyledFilterGroup>
              <label htmlFor="tg-invoice-id">{t("invoices.filter.id")}</label>
              <div style={{ display: "flex", alignItems: "center" }}>
                <input
                  id="tg-invoice-id"
                  value={invoiceIdInput}
                  onChange={e => setInvoiceIdInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleApplyInvoiceId()}
                  type="text"
                />
                <Tooltip title={t("invoices.filter.apply")}>
                  <IconButton size="small" onClick={handleApplyInvoiceId}>
                    <span className="icon-light-search" />
                  </IconButton>
                </Tooltip>
              </div>
            </StyledFilterGroup>
          </StyledFilterBlock>
        </StyledFilterDropdownWrapper>
      )}
    </>
  );

  return (
    <StyledFilterWrapper id="tg-invoices-filter">
      <Dropdown
        getPopupContainer={() => document.getElementById("tg-invoices-filter")}
        overlay={FilterList}
        trigger={["click"]}
        placement="bottomLeft"
        onVisibleChange={handleVisibleChange}
        visible={visible}
        closeOnSelect={false}
      >
        <StyledShowFilterBtn>
          <DefaultButton color="success">
            {t("invoices.filter.filterBtn")}
          </DefaultButton>
        </StyledShowFilterBtn>
      </Dropdown>
    </StyledFilterWrapper>
  );
};

export default TelegramInvoicesFilter;
